import React from 'react';
import { FieldType, SwitchLabelPosition } from 'enums/FieldTypeEnums';
import { IndexBadge } from 'shared/components/IndexBadge';
import {
  CATEGORY4CONSTANTS,
  FINISHED_GOODS,
  quantityUnits,
} from 'constants/Scope3Constants/category4.constants';
import { TemperatureControl } from 'views/CreateProductFlow/Transportation/TransportationConstants';
import { ReactComponent as DeleteButton } from 'assets/icons/close_enclosed.svg';
import styles from './finishedGoods.module.scss';
import { UseFieldArrayRemove } from 'react-hook-form';
import { SwitchChangeEventHandler } from 'antd/lib/switch';
import {
  allowOnlyNumbersUptoN,
  allowOnlyPercentages,
} from 'shared/utils/convertFormData';
import { ReactComponent as MapSearch } from 'assets/icons/map-search.svg';

const { LABEL } = FINISHED_GOODS;

const { INPUT, SELECT, SWITCH, CHECKBOX, ELEMENT } = FieldType;

const {
  PRODUCT_NAME,
  END_MARKET_LOCATION,
  PAID_BY_YOUR_COMPANY_TOGGLE,
  QUANTITY_SHIPPED,
  UNIT,
  TEMPERATURE_CONTROL,
  PAID_BY_YOUR_COMPANY,
  TRANSPORTATION_VIA_AIR,
  TOTAL_GOODS_TRANSPORTED,
  TOTAL_WEIGHT_OF_ALL_GOODS,
  GOODS_IN_STORAGE,
  GOODS_SOLD_VIA_ONLINE,
} = LABEL;

interface FinishedProductsProps {
  disabled?: boolean;
  index: number;
  remove?: UseFieldArrayRemove;
  handleSwitchChange?: (index: number) => SwitchChangeEventHandler;
  arrayLength?: number;
  toggleLocationModal?: (index: number) => void;
  validate?: () => Promise<boolean>;
  readOnly?: boolean;
}

export const getTotalFormData = (
  validate?: () => Promise<boolean>,
  readOnly?: boolean
) => {
  const ro = !!readOnly;
  return {
    data: [
      {
        type: INPUT,
        fieldProps: {
          name: 'finishedGoods.totalWeight',
          label: TOTAL_WEIGHT_OF_ALL_GOODS,
          placeholderstring: TOTAL_WEIGHT_OF_ALL_GOODS,
          onKeyDown: allowOnlyNumbersUptoN(2),
          onBlur: validate,
          isNullable: true,
          disabled: ro,
        },
        span: 4,
      },
      {
        type: INPUT,
        fieldProps: {
          name: 'finishedGoods.weightTransported',
          label: TOTAL_GOODS_TRANSPORTED,
          placeholderstring: TOTAL_GOODS_TRANSPORTED,
          onKeyDown: allowOnlyNumbersUptoN(2),
          onBlur: validate,
          isNullable: true,
          disabled: ro,
        },
        span: 4,
      },
      {
        type: INPUT,
        fieldProps: {
          name: 'finishedGoods.weightInStorage',
          label: GOODS_IN_STORAGE,
          placeholderstring: GOODS_IN_STORAGE,
          onKeyDown: allowOnlyNumbersUptoN(2),
          onBlur: validate,
          isNullable: true,
          disabled: ro,
        },
        span: 4,
      },
      {
        type: INPUT,
        fieldProps: {
          name: 'finishedGoods.weightSoldViaOnline',
          label: GOODS_SOLD_VIA_ONLINE,
          placeholderstring: GOODS_SOLD_VIA_ONLINE,
          onKeyDown: allowOnlyNumbersUptoN(2),
          onBlur: validate,
          isNullable: true,
          disabled: ro,
        },
        span: 4,
      },
    ],
    rowGutter: CATEGORY4CONSTANTS.GUTTER_VALUE,
  };
};

export const getProductFormData = (props: FinishedProductsProps) => {
  const {
    disabled,
    index,
    remove,
    handleSwitchChange,
    arrayLength,
    toggleLocationModal,
    validate,
    readOnly,
  } = props;
  const ro = !!readOnly;
  const removeProduct = () => {
    remove?.(index);
    validate?.();
  };

  const handleOpenLocationModal = () => toggleLocationModal?.(index);

  return {
    data: [
      {
        type: ELEMENT,
        additionalElement: <IndexBadge index={index} />,
        span: 1,
        className: styles.alignCenter,
      },
      {
        type: INPUT,
        fieldProps: {
          name: `finishedGoods.products[${index}].name`,
          label: PRODUCT_NAME,
          placeholderstring: PRODUCT_NAME,
          isNullable: true,
          disabled: ro,
        },
        span: 4,
      },
      {
        type: INPUT,
        fieldProps: {
          name: `finishedGoods.products[${index}].addressLabel`,
          label: END_MARKET_LOCATION,
          placeholderstring: END_MARKET_LOCATION,
          onClick: ro ? undefined : handleOpenLocationModal,
          suffix: <MapSearch />,
          readOnly: true,
          disabled: ro,
        },
        span: 4,
      },
      {
        type: SWITCH,
        fieldProps: {
          name: `finishedGoods.products[${index}].isCompletelyPaidByCompany`,
          label: PAID_BY_YOUR_COMPANY_TOGGLE,
          labelPosition: SwitchLabelPosition.RIGHT,
          onChange: handleSwitchChange?.(index),
          disabled: ro,
        },
        span: 3,
        className: styles.alignCenter,
      },
      {
        type: FieldType.ELEMENT,
        ...(arrayLength && arrayLength > 1 && !readOnly
          ? {
              additionalElement: (
                <div className={styles.closeIcon}>
                  <DeleteButton onClick={removeProduct} />
                </div>
              ),
            }
          : {}),
        span: 12,
        className: styles.alignCenter,
      },
      {
        type: ELEMENT,
        span: 1,
      },
      {
        type: INPUT,
        fieldProps: {
          name: `finishedGoods.products[${index}].quantity`,
          label: QUANTITY_SHIPPED,
          placeholderstring: QUANTITY_SHIPPED,
          onKeyDown: allowOnlyNumbersUptoN(2),
          onBlur: validate,
          isNullable: true,
          disabled: ro,
        },
        span: 3,
      },
      {
        type: SELECT,
        fieldProps: {
          name: `finishedGoods.products[${index}].unit`,
          label: UNIT,
          options: quantityUnits,
          onChange: validate,
          placeholderstring: UNIT,
          disabled: ro,
        },
        span: 2,
      },
      {
        type: SELECT,
        fieldProps: {
          name: `finishedGoods.products[${index}].temperatureControl`,
          label: TEMPERATURE_CONTROL,
          options: TemperatureControl,
          placeholderstring: TEMPERATURE_CONTROL,
          disabled: ro,
        },
        span: 4,
      },
      {
        type: INPUT,
        fieldProps: {
          name: `finishedGoods.products[${index}].companyPaymentPercentage`,
          label: PAID_BY_YOUR_COMPANY,
          placeholderstring: PAID_BY_YOUR_COMPANY,
          disabled: ro || disabled,
          onKeyDown: allowOnlyPercentages,
          isNullable: true,
        },
        span: 4,
      },
      {
        type: CHECKBOX,
        fieldProps: {
          name: `finishedGoods.products[${index}].isTransportedByAir`,
          label: TRANSPORTATION_VIA_AIR,
          disabled: ro,
        },
        span: 10,
        className: styles.alignCenter,
      },
    ],
    rowGutter: CATEGORY4CONSTANTS.GUTTER_VALUE,
  };
};
